// frontend/src/components/EventEnrollFlow.js
import React, { useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import TicketSelectionStep from './TicketSelectionStep';
import VehicleSelectionStep from './VehicleSelectionStep';
import PaymentStep from './PaymentStep';
import EnrollmentConfirmationStep from './EnrollmentConfirmationStep';
import { TICKET_PAID } from '../../constants/eventTypes';

const baseInitialState = {
  step: 1,
  selectedTicket: null,
  selectedVehicle: null,
  paymentSession: null
};

const buildInitialState = ({
  initialSelectedTicket,
  initialSelectedVehicle,
  initialPaymentSession
}) => ({
  ...baseInitialState,
  step: initialPaymentSession?.clientSecret ? 3 : 1,
  selectedTicket: initialSelectedTicket || null,
  selectedVehicle: initialSelectedVehicle || null,
  paymentSession: initialPaymentSession || null
})

function enrollReducer(state, action) {
  switch (action.type) {
    case 'SET_TICKET':
      return { ...state, selectedTicket: action.payload };
    case 'SET_VEHICLE':
      return { ...state, selectedVehicle: action.payload };
    case 'SET_STEP':
      return { ...state, step: action.payload };
    case 'SET_PAYMENT_SESSION':
      return { ...state, paymentSession: action.payload };
    case 'SET_ENROLLED_EVENT':
      return { ...state, enrolledEvent: action.payload };
    case 'RESET':
      return buildInitialState({});
    default:
      return state;
  }
}

/**
 * Se espera que el callback onEnrollComplete reciba el evento actualizado.
 * La función enrollUser se pasará desde el padre (EventDetail) y debe retornar el evento actualizado.
 */
const EventEnrollFlow = ({
  event,
  onEnrollComplete,
  onCancel,
  enrollUser,
  onViewDetails,
  onPaymentSessionCreated,
  onPaidCheckoutStateChange,
  initialSelectedTicket,
  initialSelectedVehicle,
  initialPaymentSession
}) => {
  const [state, dispatch] = useReducer(
    enrollReducer,
    {
      initialSelectedTicket,
      initialSelectedVehicle,
      initialPaymentSession
    },
    buildInitialState
  );
  const { step, selectedTicket, selectedVehicle, paymentSession } = state;
  const navigate = useNavigate();

  const handleAuthError = () => {
    toast.error('Debes iniciar sesión para inscribirte.');
    navigate('/signin', { state: { returnTo: window.location.pathname } });
  };

  // Paso 1: Selección de Ticket
  if (step === 1) {
    return (
      <TicketSelectionStep
        tickets={event.tickets}
        onTicketSelected={(ticket) => {
          // Guardamos el ticket seleccionado en el estado
          dispatch({ type: 'SET_TICKET', payload: ticket });
          // Si el evento requiere vehículo, pasamos al paso 2
          if (event.needsVehicle) {
            dispatch({ type: 'SET_STEP', payload: 2 });
          } else if (ticket.type === TICKET_PAID) {
            // Para tickets de pago sin vehículo requerido, ir al paso de pago
            dispatch({ type: 'SET_STEP', payload: 3 });
            // } else {
            //   // Para tickets gratuitos sin vehículo requerido, ejecutamos la inscripción directamente
            //   enrollUser(ticket, null)
            //     .then((updatedEvent) => {
            //       onEnrollComplete(updatedEvent);
            //       dispatch({ type: 'SET_STEP', payload: 4 });
            //     })
            //     .catch((err) => {
            //       console.error('Error en inscripción gratuita:', err);
            //     });
            // }
          } else {
            // 1) Disparamos la inscripción
            enrollUser(ticket, null)
              .then((updatedEvent) => {
                // 2) Guardamos en state local el evento actualizado
                dispatch({ type: 'SET_ENROLLED_EVENT', payload: updatedEvent });
                // 3) Avanzamos al paso 4 para mostrar confirmación
                dispatch({ type: 'SET_STEP', payload: 4 });
              })
              // .catch((err) => { console.error('Error en inscripción gratuita:', err); });
              .catch((err) => {
                if (err.response?.status === 401) {
                  handleAuthError();
                } else {
                  console.error('Error en inscripción gratuita:', err);
                  toast.error('Error al inscribirse. Intenta de nuevo.');
                }
              });
          }
        }}
        onCancel={onCancel}
      />
    );
  }

  // Paso 2: Selección de Vehículo (para eventos que requieren vehículo)
  if (step === 2) {
    return (
      <VehicleSelectionStep
        allowedVehicleTypes={event.allowedVehicleTypes}
        onVehicleSelected={(vehicle) => {
          // Al seleccionar el vehículo, se guarda en el estado
          dispatch({ type: 'SET_VEHICLE', payload: vehicle });
          // Ahora, para tickets gratuitos (ya que para pagos se iría al paso 3) se ejecuta la inscripción
          if (selectedTicket && selectedTicket.type !== TICKET_PAID) {
            enrollUser(selectedTicket, vehicle)
              .then((updatedEvent) => {
                // onEnrollComplete(updatedEvent);
                dispatch({ type: 'SET_ENROLLED_EVENT', payload: updatedEvent });
                dispatch({ type: 'SET_STEP', payload: 4 });
              })
              // .catch((err) => {
              //   console.error('Error en inscripción gratuita con vehículo:', err);
              // });
              .catch((err) => {
                if (err.response?.status === 401) {
                  handleAuthError();
                } else {
                  console.error('Error en inscripción gratuita con vehículo:', err);
                  toast.error('Error al inscribirse. Intenta de nuevo.');
                }
              });
          } else {
            // Si el ticket es de pago, pasamos al paso de pago
            dispatch({ type: 'SET_STEP', payload: 3 });
          }
        }}
        onCancel={onCancel}
        eventRequiresVehicle={event.needsVehicle}
      />
    );
  }

  // Paso 3: Pago (para tickets de pago)
  if (step === 3 && selectedTicket && selectedTicket.type === TICKET_PAID) {
    return (
      <PaymentStep
        eventId={event.id}
        ticket={selectedTicket}
        vehicle={selectedVehicle}
        initialPaymentSession={paymentSession}
        onPaymentSessionCreated={(session) => {
          dispatch({ type: 'SET_PAYMENT_SESSION', payload: session });
          onPaymentSessionCreated?.({
            ...session,
            ticketId: selectedTicket?._id || null,
          });
        }}
        onPaymentStateChange={(paymentContext) => {
          onPaidCheckoutStateChange?.({
            ...paymentContext,
            ticketId: selectedTicket?._id || null,
          });
          onCancel();
        }}
        onCancel={onCancel}
      />
    );
  }

  // Paso 4: Confirmación de Inscripción
  if (step === 4) {
    return (
      <EnrollmentConfirmationStep
        enrollmentData={{ ticket: selectedTicket, vehicle: selectedVehicle }}
        event={state.enrolledEvent || event}
        eventLink={`${process.env.REACT_APP_CLIENT_URL}/events/${event.id}/${event.slug || event.title.replace(/\s+/g, '-').toLowerCase()}`}
        onComplete={() => {
          onEnrollComplete(state.enrolledEvent);
          dispatch({ type: 'RESET' });
        }}
        onCancel={onCancel}
        onViewDetails={onViewDetails}
      />
    );
  }

  return null;
};

EventEnrollFlow.propTypes = {
  event: PropTypes.object.isRequired,
  onEnrollComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  // enrollUser es la función que ejecuta la inscripción y devuelve el evento actualizado
  enrollUser: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
  onPaymentSessionCreated: PropTypes.func,
  onPaidCheckoutStateChange: PropTypes.func,
  initialSelectedTicket: PropTypes.object,
  initialSelectedVehicle: PropTypes.object,
  initialPaymentSession: PropTypes.shape({
    clientSecret: PropTypes.string,
    connectedAccountId: PropTypes.string,
    paymentId: PropTypes.string,
    reservationId: PropTypes.string,
  }),
};

export default EventEnrollFlow;
